import React from "react";
import { Card } from "primereact/card";
import { Message } from "primereact/message";
import { Tag } from "primereact/tag";
import type { DecompositionReport, DecompositionStep } from "../../../types/stationPerformance";

interface MoneyChangeWaterfallProps {
  report: DecompositionReport;
}

const euro = (cents: number) =>
  (cents / 100).toLocaleString(undefined, { style: "currency", currency: "EUR" });

const signedEuro = (cents: number) => `${cents > 0 ? "+" : ""}${euro(cents)}`;

/**
 * Why money per active unit moved (SC-006).
 *
 * A bar chart would be the obvious choice and the wrong one: the steps are signed contributions that must
 * visibly *reconstruct* the observed change, and a chart makes the reader estimate whether they add up. So
 * this renders proportional bars alongside the exact figures, with the residual shown: the point is that
 * the arithmetic closes, not that it looks tidy.
 *
 * The distinction being drawn is the one CP-388 reconstructed by hand: a falling blended figure means
 * either mature units declining or new low-output units diluting the average. Only the same-store step
 * speaks to the former, and conflating them is how "our stations are getting worse" gets said when the
 * truth is "we opened a lot of new ones".
 */
const MoneyChangeWaterfall: React.FC<MoneyChangeWaterfallProps> = ({ report }) => {
  const steps = report.steps ?? [];
  // Scale bars against the largest magnitude on screen, including the total, so the total is never
  // dwarfed by a step and the relative sizes stay honest.
  const scale = Math.max(
    ...steps.map((s) => Math.abs(s.effectCents)),
    Math.abs(report.observedChangeCents),
    1,
  );

  const barFor = (cents: number) => {
    const width = `${Math.max((Math.abs(cents) / scale) * 100, 1)}%`;
    const positive = cents >= 0;
    return (
      <div className="flex align-items-center" style={{ height: "1.25rem" }}>
        <div className="flex justify-content-end" style={{ width: "50%" }}>
          {!positive && (
            <div className="bg-red-400 border-round-left" style={{ width, height: "100%" }} />
          )}
        </div>
        <div style={{ width: "50%" }}>
          {positive && <div className="bg-green-400 border-round-right" style={{ width, height: "100%" }} />}
        </div>
      </div>
    );
  };

  const rowFor = (step: DecompositionStep) => {
    const unitCount =
      step.key === "entry"
        ? report.enteringUnits
        : step.key === "exit"
          ? report.exitingUnits
          : report.sameStoreUnits;
    return (
      <div key={step.key} className="grid align-items-center mb-2">
        <div className="col-12 md:col-3">
          <div className="font-medium">{step.label}</div>
          <div className="text-color-secondary text-xs">
            {unitCount} unit{unitCount === 1 ? "" : "s"}
          </div>
        </div>
        <div className="col-12 md:col-5">{barFor(step.effectCents)}</div>
        <div className="col-6 md:col-2 text-right font-medium">{signedEuro(step.effectCents)}</div>
        <div className="col-12 md:col-2">
          <i
            className="pi pi-info-circle text-color-secondary"
            style={{ fontSize: "0.75rem" }}
            title={step.explanation}
          />
        </div>
      </div>
    );
  };

  const sameStore = steps.find((s) => s.key === "sameStore");
  const freeShare = report.freeShareEffectWithinSameStoreCents ?? 0;
  // Exactly zero, not "within a cent": the backend apportions its rounding across the three steps so
  // they sum to the observed change precisely. A tolerance here would hide the one thing this panel
  // exists to show: that the arithmetic closes.
  const residualIsClosed = (report.residualCents ?? 0) === 0;

  return (
    <Card title={`Why money per unit moved: ${report.fromMonth} → ${report.toMonth}`}>
      <div className="flex flex-wrap gap-4 mb-3 text-sm">
        <span>
          {report.fromMonth}: <strong>{euro(report.moneyPerUnitFromCents)}</strong> per unit
          <span className="text-color-secondary"> ({report.activeUnitsFrom} active)</span>
        </span>
        <span>
          {report.toMonth}: <strong>{euro(report.moneyPerUnitToCents)}</strong> per unit
          <span className="text-color-secondary"> ({report.activeUnitsTo} active)</span>
        </span>
        <span>
          Change:{" "}
          <strong className={report.observedChangeCents >= 0 ? "text-green-600" : "text-red-600"}>
            {signedEuro(report.observedChangeCents)}
          </strong>
        </span>
      </div>

      {steps.map(rowFor)}

      <div className="grid align-items-center border-top-1 surface-border pt-2 mt-2">
        <div className="col-12 md:col-3 font-semibold">Observed change</div>
        <div className="col-12 md:col-5">{barFor(report.observedChangeCents)}</div>
        <div className="col-6 md:col-2 text-right font-semibold">
          {signedEuro(report.observedChangeCents)}
        </div>
        <div className="col-12 md:col-2">
          {residualIsClosed ? (
            <Tag value="adds up" severity="success" title="The three effects reconstruct the observed change exactly" />
          ) : (
            <Tag
              value={`residual ${signedEuro(report.residualCents)}`}
              severity="danger"
              title="The effects do not reconstruct the observed change — treat this decomposition as unreliable"
            />
          )}
        </div>
      </div>

      {sameStore && freeShare !== 0 && (
        <Message
          className="mt-3 w-full"
          severity="info"
          text={`Of the ${signedEuro(sameStore.effectCents)} same-store move, ${signedEuro(freeShare)} is attributable to sessions shifting between paid and free. That is a pricing change, not a performance change — it sits inside same-store rather than beside it.`}
        />
      )}

      {report.enteringUnits > 0 && report.observedChangeCents < 0 && (
        <Message
          className="mt-2 w-full"
          severity="info"
          text={`${report.enteringUnits} unit(s) entered this period. New units typically start below the fleet average, so a falling per-unit figure can reflect that dilution rather than any decline at existing units — compare the same-store step before concluding otherwise.`}
        />
      )}
    </Card>
  );
};

export default MoneyChangeWaterfall;
